<template>
  <div class="cis-carousel">
    <!-- Optional: if you want to expose the heading field (hidden visually but available to AT) -->
    <sc-text
      v-if="fields && fields.heading"
      tag="h2"
      class="visually-hidden"
      :field="fields.heading"
    />

    <!-- Experience Editor: render as a simple, stacked list for easier editing -->
    <template v-if="isEditing">
      <div class="cis-carousel__frame" :style="{ '--cis-carousel-height': computedHeight }">
        <div v-for="(slide, index) in slides" :key="index" class="cis-carousel__slide is-editing">
          <cis-carousel-slide :slide="slide" :in-editor="true" />
        </div>
      </div>
    </template>

    <!-- Live mode: Splide carousel -->
    <template v-else>
      <div class="cis-carousel__content">
        <Splide
          v-if="slides.length > 0"
          class="cis-carousel__splide"
          :options="options"
          aria-label="Hero carousel"
          @splide:mounted="onSplideMove"
          @splide:move="onSplideMove"
        >
          <SplideSlide v-for="(slide, index) in slides" :key="index">
            <cis-carousel-slide :slide="slide" :slide-index="index" />
          </SplideSlide>
        </Splide>
      </div>
    </template>
  </div>
</template>

<script>
import { Text } from '@sitecore-jss/sitecore-jss-vue';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';

import CisCarouselSlide from './CIS-Carousel-Slide.vue';

export default {
  name: 'CIS-Carousel',
  components: {
    ScText: Text,
    Splide,
    SplideSlide,
    CisCarouselSlide
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    // Sitecore Experience Editor check (same approach used in your other carousel)
    isEditing() {
      return this.$jss?.sitecoreContext()?.pageEditing === true;
    },
    slides() {
      return this.rendering?.fields?.items || [];
    },
    // Let height be tunable if you want. Defaults to full viewport height.
    computedHeight() {
      return '100vh';
    },
    options() {
      const DEFAULT_AUTOPLAY = true;
      const DEFAULT_INTERVAL = 5000;
      const DEFAULT_PAUSE_ON_HOVER = true;

      const autoplay = this.fields?.autoRotate?.value ?? DEFAULT_AUTOPLAY;

      const interval =
        typeof this.fields?.rotateIntervalMs?.value === 'number'
          ? this.fields.rotateIntervalMs.value
          : DEFAULT_INTERVAL;

      const pauseOnHover = this.fields?.pauseOnHover?.value ?? DEFAULT_PAUSE_ON_HOVER;

      return {
        type: 'loop',
        perPage: 1,
        perMove: 1,
        rewind: true,

        autoplay,
        interval,
        pauseOnHover,

        pauseOnFocus: true,
        resetProgress: false,

        arrows: true,
        pagination: true,

        speed: 800,
        updateOnMove: true,
        drag: true,
        preventClick: false
      };
    }
  },
  methods: {
    onSplideMove(splide) {
      if (this.isEditing) return;

      const nextIndex = splide.index + 1;
      const nextSlide = this.slides[nextIndex];
      const src = nextSlide?.fields?.image?.value?.src;

      if (!src) return;

      // Prevent duplicate preload
      if (document.querySelector(`link[data-preload="${src}"]`)) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.setAttribute('data-preload', src);
      document.head.appendChild(link);
    }
  }
};
</script>

<style lang="scss" scoped>
/* ========== Structure & Sizing ========== */
.cis-carousel {
  /* Full width container; black background prevents awkward edges on wide screens */
  width: 100%;
  background: transparent;
}

/* A visually hidden utility for headings */
.visually-hidden {
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
  border: 0;
  padding: 0;
  margin: -1px;
}

/* Splide host */
.cis-carousel__splide {
  width: 100%;
}

/* Slide frame: center and box at max 1600px, but stretch to viewport height */
.cis-carousel__frame {
  position: relative;
  max-width: 1600px;
  margin: 0 auto;
  // height: var(--cis-carousel-height, 100vh);
}

/* Editor stack layout */
.cis-carousel__slide.is-editing + .cis-carousel__slide.is-editing {
  margin-top: 2rem;
}

/* ========== Splide overrides: Arrows & Dots ========== */
.cis-carousel :deep(.splide__arrow) {
  background: transparent;
  border: 0;
  width: 3.6rem;
  height: 3.6rem;
  opacity: 1;
  top: 50%;
  transform: translateY(-50%);
}

.cis-carousel :deep(.splide__arrow--prev) {
  left: 1rem;
}

.cis-carousel :deep(.splide__arrow--next) {
  right: 1rem;
}

.cis-carousel :deep(.splide__arrow svg) {
  fill: #fff;
}

/* Dots centered at bottom, active solid white; inactive white outline */
.cis-carousel :deep(.splide__pagination) {
  bottom: 2rem;
}

.cis-carousel :deep(.splide__pagination__page) {
  width: 10px;
  height: 10px;
  margin: 0 6px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid #fff;
  opacity: 1;
  transform: none; /* prevent scale animation */
}

.cis-carousel :deep(.splide__pagination__page.is-active) {
  background: #fff;
  border-color: #fff;
}

/* Simple mobile height tweak if desired */
@media (max-width: 480px) {
  .cis-carousel__frame {
    height: 70vh; /* keep mobile hero a bit shorter */
    min-height: 420px;
  }
}
.cis-carousel {
  &:deep(.cis-carousel__content) {
    @include padding-x-thin;
    @include margin-x(auto);
  }
}
.cis-carousel {
  /* Arrow offset — intentionally tighter */
  --carousel-arrow-offset: clamp(0.25%, 0.5vw, 0.5%);
}
</style>

<style lang="scss">
/* UN-SCOPED block for Splide controls so it always wins */
/* Make sure the host is a positioning context for the dots */
.cis-carousel .splide {
  position: relative !important;
}

/* -------- Arrows -------- */
.cis-carousel .splide .splide__arrow {
  background: transparent !important;
  border: 0 !important;
  width: 3rem !important;
  height: 3rem !important;
  opacity: 1 !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}

.cis-carousel .splide .splide__arrow--prev {
  left: var(--carousel-arrow-offset) !important;
}

.cis-carousel .splide .splide__arrow--next {
  right: var(--carousel-arrow-offset) !important;
}

.cis-carousel .splide .splide__arrow svg {
  fill: #ddd !important;
}

/* -------- Pagination (Dots) -------- */
/* Center near the bottom of the slide */
.cis-carousel .splide .splide__pagination {
  position: absolute !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  bottom: 2rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: clamp(1px, 0.5vw, 12px) !important; /* space between dots */
  padding: 0 !important;
  margin: 0 !important;
  right: auto !important; /* prevent themes setting right:0 */
}

/* Make each dot a white outline circle by default */
.cis-carousel .splide .splide__pagination__page,
.cis-carousel .splide button.splide__pagination__page {
  width: clamp(7.5px, 1vw, 16px) !important;
  height: clamp(7.5px, 1vw, 16px) !important;
  border-radius: 50% !important;
  background: #444 !important;
  border: 2px solid #fff !important;
  opacity: 1 !important;
  transform: none !important; /* disable scale animation if any */
  /* box-shadow: none !important; */
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.55) !important;
  appearance: none !important; /* avoid “switch” look */
  -webkit-appearance: none !important;
  outline: none !important;
  cursor: pointer !important;
}

/* Active dot is solid white */
.cis-carousel .splide .splide__pagination__page.is-active,
.cis-carousel .splide button.splide__pagination__page.is-active {
  background: #fff !important;
  border-color: #fff !important;
}

/* Optional hover for better UX */
.cis-carousel .splide .splide__pagination__page:hover,
.cis-carousel .splide button.splide__pagination__page:hover {
  background: #999 !important;
}

/* Remove any theme-provided pseudo bullets that overlay on the button */
.cis-carousel .splide .splide__pagination__page::before,
.cis-carousel .splide .splide__pagination__page::after,
.cis-carousel .splide button.splide__pagination__page::before,
.cis-carousel .splide button.splide__pagination__page::after {
  content: none !important;
  display: none !important;
}

/**** For L/R arrow sizes ****/
/* Expand the clickable area and ensure the icon stays centered */
.cis-carousel .splide .splide__arrow {
  width: clamp(1.5rem, 5vw, 7rem) !important; /* increase hitbox 6 big*/
  height: clamp(1.5rem, 5vw, 7rem) !important;
  padding: 0 !important; /* remove theme padding that could shift icons */
  display: grid !important; /* center the svg inside */
  place-items: center !important;
  box-sizing: content-box !important;
}

/* Scale the actual chevron SVG */
.cis-carousel .splide .splide__arrow svg {
  width: clamp(1rem, 3vw, 4.2rem) !important; /* increase icon size 4.2 big*/
  height: clamp(1rem, 3vw, 4.2rem) !important;
  fill: #fff !important; /* your current color */
  opacity: 0.75;
  flex: 0 0 auto !important;
  display: block !important;
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.55));
}
.cis-carousel .splide .splide__arrow svg:hover {
  opacity: 1;
}

/* If your theme adds a background image/mask or a pseudo-element, neutralize it */
.cis-carousel .splide .splide__arrow::before,
.cis-carousel .splide .splide__arrow::after {
  content: none !important;
  background: none !important;
  mask: none !important;
  -webkit-mask: none !important;
  border: 0 !important;
  box-shadow: none !important;
}

@media (max-width: 440px) {
  .cis-carousel .splide .splide__pagination,
  .cis-carousel .splide .splide__pagination__page,
  .cis-carousel .splide button.splide__pagination__page {
    display: none;
  }
}
</style>
